import { useEffect, useState } from 'react';

/**
 * Add-in auth state. Mirrors the workspace auth flow.
 *
 * The taskpane runs inside Word; we use localStorage (Office sandbox grants
 * access to it) so the user stays signed in across taskpane reopens.
 *
 * Until SSO ships, we authenticate with email/password against
 * /api/v1/auth/login and store the resulting JWT.
 */

const ACCESS_KEY = 'docplugin_addin_access_token';
const REFRESH_KEY = 'docplugin_addin_refresh_token';
const USER_KEY = 'docplugin_addin_user';

// `process.env.X` references here are statically replaced by webpack
// DefinePlugin at build time (see apps/addin/webpack.config.js). The
// add-in runs in the browser, so we don't depend on Node's global
// process — we just declare the shape we expect after replacement so
// the TypeScript compiler doesn't complain.
declare const process: { env: { ADDIN_API_URL?: string; ADDIN_API_KEY?: string } };

// Resolved at build time from the ADDIN_API_URL env var via webpack DefinePlugin.
// Falls back to localhost:4000 for local dev when no env override is set.
export const API_URL = process.env.ADDIN_API_URL || 'http://localhost:4000/api/v1';

export interface AuthUser {
  userId: number;
  tenantId: number;
  email: string;
  role: string;
  permissions: string[];
}

interface JwtPayload extends AuthUser {
  iat: number;
  exp: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export const auth = {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },

  getUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  },

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    const payload = decodeJwt(token);
    if (!payload) return false;
    return payload.exp * 1000 > Date.now();
  },

  hasPermission(perm: string): boolean {
    const user = this.getUser();
    if (!user) return false;
    return user.permissions.includes(perm);
  },

  async login(email: string, password: string): Promise<AuthUser> {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const body = await res.json();
    if (!res.ok || !body.success) {
      throw new Error(body?.error?.message || 'Login failed');
    }
    const { accessToken, refreshToken } = body.data;
    const payload = decodeJwt(accessToken);
    if (!payload) throw new Error('Invalid token from server');
    const user: AuthUser = {
      userId: payload.userId,
      tenantId: payload.tenantId,
      email: payload.email,
      role: payload.role,
      permissions: payload.permissions ?? [],
    };
    localStorage.setItem(ACCESS_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    window.dispatchEvent(new Event('docplugin:auth-changed'));
    return user;
  },

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    window.dispatchEvent(new Event('docplugin:auth-changed'));
    if (refreshToken) {
      fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
  },
};

/**
 * Returns the auth headers to attach to every API request.
 * - When logged in: Authorization: Bearer <jwt>
 * - When not logged in: nothing (callers should ensure they're logged in first)
 *
 * Used by every fetch site in the add-in instead of the old hardcoded
 * `X-API-Key: dp_dev_howden_key123`. The X-API-Key path is kept as an
 * emergency fallback for components that haven't been migrated yet.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = auth.getAccessToken();
  if (token && auth.isAuthenticated()) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

/** Hook that re-renders on login/logout and exposes permission checks. */
export function useAuth(): {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (...perms: string[]) => boolean;
} {
  const [user, setUser] = useState<AuthUser | null>(auth.getUser());

  useEffect(() => {
    const handler = () => setUser(auth.getUser());
    window.addEventListener('docplugin:auth-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('docplugin:auth-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const granted = user?.permissions ?? [];
  return {
    user,
    isAuthenticated: auth.isAuthenticated() && user !== null,
    hasPermission: (perm) => granted.includes(perm),
    hasAnyPermission: (...perms) => perms.some((p) => granted.includes(p)),
  };
}
